// examenEspera.js — prueba del examen práctico (pista / circulación), aviso a la OFICINA al apuntar a un alumno a
// examen y LISTA DE ESPERA para examen (22/10/2026, Autoescola Ribas: permisos de pesados, #30f y #30l).
// Ficha: docs/features/autoescuela-examen-oficina-y-lista-espera.md
//
//   · c_examenes.prueba: "pista" | "circulacion" (solo con tipo "practico"; ausente = como siempre). En pesados el
//     práctico son DOS pruebas y cada una lleva su nº de intento.
//   · c_autoescuela_config.aviso_oficina_examen = { enabled, telefonos: [E.164 ×3], pruebas: [...], plazo_pago_dias }:
//     al apuntar a un alumno a un examen que encaje, WhatsApp a la oficina para que le pida el pago antes de X.
//   · c_autoescuela_config.lista_espera_examen = { enabled, aviso_dias }: reglas automáticas (apto de pista → lista de
//     circulación; no apto → vuelve a la suya) y aviso en la campanita a quien pase de N días esperando.
//   · c_alumnos.espera_examen = { prueba, desde, especial, motivo, origen } (ausente = en ninguna lista).
//
// PURO (sin AWS ni fechas del sistema: «hoy» siempre entra por parámetro). CANÓNICO: se edita SOLO aquí y luego
//   node aws_lambdas/_shared_autoescuela/examenEsperaCopias.js
// (copia por valor en c_putExamen, c_putAutoescuelaConfig y c_schedulerAlumnoAvisos; en módulo ES en la app y en el
// simulador). examenEsperaCopias.test.js falla si alguna diverge.

const esObjeto = (v) => !!v && typeof v === "object" && !Array.isArray(v);
const YMD_RE = /^(\d{4})-(\d{2})-(\d{2})$/;
const DIA_MS = 86400000;

/* ───────────── Prueba del examen práctico ───────────── */

const PRUEBAS_PRACTICO = ["pista", "circulacion"];
/** Las listas de espera: las dos pruebas del práctico y el teórico. */
const PRUEBAS_ESPERA = ["pista", "circulacion", "teorico"];

/**
 * Lo que llega en el body → { value } (undefined = no viene, se conserva; null = quitarla) | { error }.
 * Solo vale con un examen práctico: con un teórico, una prueba es un error (no se guarda coaccionada).
 */
function validaPrueba(tipo, raw) {
  if (raw === undefined) return { value: undefined };
  if (raw === null || raw === "") return { value: null };
  if (!PRUEBAS_PRACTICO.includes(raw)) return { error: "prueba inválida (pista|circulacion)" };
  if (tipo !== "practico") return { error: "La prueba (pista o circulación) solo se elige en un examen práctico." };
  return { value: raw };
}

/**
 * Nº de intento de un examen NUEVO: los anteriores del mismo alumno y tipo (+1). Con prueba, solo los de esa prueba
 * (en pesados, suspender pista no gasta un intento de circulación). Sin prueba, como siempre: todos los del tipo.
 */
function numeroIntento(previos, tipo, prueba) {
  const n = (previos || []).filter((e) => e && e.tipo === tipo && (!prueba || e.prueba === prueba)).length;
  return n + 1;
}

/** De qué lista de espera es un examen: teorico, o la prueba del práctico (sin prueba = circulación, la del B). */
function pruebaDeExamen(examen) {
  if (!examen) return null;
  if (examen.tipo === "teorico") return "teorico";
  if (examen.tipo === "practico") return PRUEBAS_PRACTICO.includes(examen.prueba) ? examen.prueba : "circulacion";
  return null;
}

/* ───────────── Fechas y textos ───────────── */

const ymdUtc = (ymd) => {
  const m = YMD_RE.exec(String(ymd || ""));
  return m ? Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3])) : null;
};
const aYmd = (ms) => new Date(ms).toISOString().slice(0, 10);
/** ¿Fecha real «YYYY-MM-DD»? (31/02 no). */
function esFecha(ymd) {
  const ms = ymdUtc(ymd);
  return ms != null && aYmd(ms) === String(ymd);
}
/** Días desde `desde` hasta `hoy` (0 si es hoy; nunca negativo). null sin fechas válidas. */
function diasEntre(desde, hoy) {
  const a = ymdUtc(desde); const b = ymdUtc(hoy);
  if (a == null || b == null) return null;
  return Math.max(0, Math.round((b - a) / DIA_MS));
}

const DIAS = {
  es: ["domingo", "lunes", "martes", "miércoles", "jueves", "viernes", "sábado"],
  ca: ["diumenge", "dilluns", "dimarts", "dimecres", "dijous", "divendres", "dissabte"],
};
/** «viernes 31/10/2026» (ca: «divendres 31/10/2026»). */
function fechaLarga(ymd, lang) {
  const ms = ymdUtc(ymd);
  if (ms == null) return String(ymd || "");
  const [y, m, d] = String(ymd).split("-");
  return `${DIAS[lang === "ca" ? "ca" : "es"][new Date(ms).getUTCDay()]} ${d}/${m}/${y}`;
}

/** Fecha límite del pago: fecha del examen − plazo; si cae antes de hoy, hoy. */
function fechaLimitePago(fechaExamen, plazoDias, hoy) {
  const ms = ymdUtc(fechaExamen);
  if (ms == null) return hoy;
  const lim = aYmd(ms - (Number(plazoDias) || 0) * DIA_MS);
  return hoy && lim < hoy ? hoy : lim;
}

const NOMBRES_PRUEBA = {
  es: { pista: "de pista", circulacion: "de circulación", practico: "práctico", teorico: "teórico", permiso: "permiso" },
  ca: { pista: "de pista", circulacion: "de circulació", practico: "pràctic", teorico: "teòric", permiso: "permís" },
};
/** «práctico de pista, permiso C» · «teórico, permiso B» · «práctico» (ca: «pràctic de pista, permís C»). */
function descripcionExamen(examen, permiso, lang) {
  const T = NOMBRES_PRUEBA[lang === "ca" ? "ca" : "es"];
  const base = examen && examen.tipo === "teorico" ? T.teorico : T.practico;
  const conPrueba = examen && examen.tipo === "practico" && PRUEBAS_PRACTICO.includes(examen.prueba) ? `${base} ${T[examen.prueba]}` : base;
  const p = String(permiso || "").trim();
  return p ? `${conPrueba}, ${T.permiso} ${p}` : conPrueba;
}

/** Un parámetro de plantilla limpio: Meta no admite saltos de línea, tabuladores ni más de 4 espacios seguidos. */
function limpiaParam(s, max = 120) {
  const t = String(s == null ? "" : s).replace(/[\r\n\t]+/g, " ").replace(/ {2,}/g, " ").trim();
  return t.length > max ? `${t.slice(0, max - 1)}…` : t;
}

/* ───────────── Aviso a la oficina ───────────── */

const OFICINA_PRUEBAS = ["pista", "circulacion", "practico", "teorico"];
const MAX_TELEFONOS = 3;
const PLAZO_PAGO = [0, 30];
const AVISO_OFICINA_DEFECTO = Object.freeze({ enabled: false, telefonos: [], pruebas: ["pista"], plazo_pago_dias: 7 });

/** «+34600111222» | «600111222» | «0034…» | «+351…» → «+34600111222» o null. E.164 o nacional español. */
function normalizaTelefono(raw) {
  let s = String(raw == null ? "" : raw).replace(/[\s\-().]/g, "");
  if (!s) return null;
  if (s.startsWith("00")) s = `+${s.slice(2)}`;
  if (s.startsWith("+")) {
    if (!/^\+[1-9]\d{6,14}$/.test(s)) return null;
    if (s.startsWith("+34") && !/^\+34[6-9]\d{8}$/.test(s)) return null;
    return s;
  }
  if (/^34[6-9]\d{8}$/.test(s)) return `+${s}`;
  return /^[6-9]\d{8}$/.test(s) ? `+34${s}` : null;
}

/** Siempre completa (nunca lanza): lo que lee quien envía. */
function normalizaAvisoOficina(raw) {
  const r = esObjeto(raw) ? raw : {};
  const tels = [];
  for (const t of Array.isArray(r.telefonos) ? r.telefonos : []) {
    const n = normalizaTelefono(t);
    if (n && !tels.includes(n) && tels.length < MAX_TELEFONOS) tels.push(n);
  }
  const pruebas = (Array.isArray(r.pruebas) ? r.pruebas : []).filter((p, i, a) => OFICINA_PRUEBAS.includes(p) && a.indexOf(p) === i);
  const plazo = Number(r.plazo_pago_dias);
  return {
    enabled: r.enabled === true,
    telefonos: tels,
    pruebas: pruebas.length ? pruebas : [...AVISO_OFICINA_DEFECTO.pruebas],
    plazo_pago_dias: Number.isInteger(plazo) && plazo >= PLAZO_PAGO[0] && plazo <= PLAZO_PAGO[1] ? plazo : AVISO_OFICINA_DEFECTO.plazo_pago_dias,
  };
}

/** Lo que llega del navegador → { value } | { error } (la forma equivocada se rechaza, no se coacciona). */
function validaAvisoOficina(raw) {
  if (raw === null) return { value: { ...AVISO_OFICINA_DEFECTO, telefonos: [], pruebas: [...AVISO_OFICINA_DEFECTO.pruebas] } };
  if (!esObjeto(raw)) return { error: "aviso_oficina_examen no tiene el formato esperado." };
  if (raw.enabled !== undefined && typeof raw.enabled !== "boolean") return { error: "aviso_oficina_examen.enabled debe ser true o false." };
  if (raw.telefonos !== undefined && !Array.isArray(raw.telefonos)) return { error: "Los teléfonos de la oficina van en una lista." };
  const tels = (raw.telefonos || []).filter((t) => String(t == null ? "" : t).trim() !== "");
  if (tels.length > MAX_TELEFONOS) return { error: `Como mucho ${MAX_TELEFONOS} teléfonos de la oficina.` };
  for (const t of tels) if (!normalizaTelefono(t)) return { error: `Revisa el teléfono «${String(t).slice(0, 20)}»: con prefijo (+34…) o 9 cifras.` };
  if (raw.pruebas !== undefined) {
    if (!Array.isArray(raw.pruebas) || raw.pruebas.some((p) => !OFICINA_PRUEBAS.includes(p))) {
      return { error: "Qué exámenes avisan: pista, circulacion, practico o teorico." };
    }
    if (raw.enabled === true && raw.pruebas.length === 0) return { error: "Elige al menos un examen que avise a la oficina." };
  }
  if (raw.plazo_pago_dias !== undefined && !(Number.isInteger(raw.plazo_pago_dias) && raw.plazo_pago_dias >= PLAZO_PAGO[0] && raw.plazo_pago_dias <= PLAZO_PAGO[1])) {
    return { error: `Días de margen para el pago: un número entre ${PLAZO_PAGO[0]} y ${PLAZO_PAGO[1]}.` };
  }
  const value = normalizaAvisoOficina({ ...raw, telefonos: tels });
  if (value.enabled && !value.telefonos.length) return { error: "Pon al menos un teléfono de la oficina para poder avisarla." };
  return { value };
}

/** ¿Este examen avisa a la oficina? «practico» = cualquier práctico; «pista»/«circulacion» = esa prueba. */
function encajaAvisoOficina(cfg, examen) {
  const c = normalizaAvisoOficina(cfg);
  if (!c.enabled || !c.telefonos.length || !examen) return false;
  if (examen.tipo === "teorico") return c.pruebas.includes("teorico");
  if (examen.tipo !== "practico") return false;
  if (c.pruebas.includes("practico")) return true;
  return PRUEBAS_PRACTICO.includes(examen.prueba) && c.pruebas.includes(examen.prueba);
}

/**
 * ¿Toca avisar AHORA? Examen nuevo, o con la fecha cambiada, aún por hacer, que encaja y no avisado ya para esa fecha.
 * `prev` = el examen antes de guardar (null en el alta).
 */
function tocaAvisoOficina({ cfg, examen, prev, hoy }) {
  if (!examen || examen.status !== "pendiente") return false;
  if (!encajaAvisoOficina(cfg, examen)) return false;
  if (hoy && String(examen.fecha || "") < hoy) return false; // un examen ya pasado no pide pago
  if (examen.aviso_oficina_fecha === examen.fecha) return false;
  // Misma fecha: solo si ANTES no encajaba (le acaban de poner «pista», p. ej.); si ya encajaba, ya tocó (o no tocaba).
  if (prev && String(prev.fecha || "") === String(examen.fecha || "") && encajaAvisoOficina(cfg, prev)) return false;
  return true;
}

/** «Carlos Pérez (+34 600 111 222)». */
function etiquetaAlumno(nombre, phone) {
  const d = String(phone == null ? "" : phone).replace(/\D/g, "");
  const tel = d.startsWith("34") && d.length === 11 ? `+34 ${d.slice(2, 5)} ${d.slice(5, 8)} ${d.slice(8)}` : d ? `+${d}` : "";
  const n = limpiaParam(nombre, 60);
  return n && tel ? `${n} (${tel})` : n || tel || "Alumno";
}

/**
 * Parámetros de examen_aviso_oficina_crm (c_sendTemplate antepone el negocio en {{1}} con la _trial):
 * { 1: alumno, 2: examen, 3: fecha del examen, 4: fecha límite del pago, url_button_param: alumno_id }.
 */
function paramsAvisoOficina({ alumno, examen, cfg, hoy, lang }) {
  const c = normalizaAvisoOficina(cfg);
  const a = alumno || {};
  return {
    1: etiquetaAlumno(a.name || (examen && examen.alumno_name), a.phone),
    2: limpiaParam(descripcionExamen(examen, a.carnet_type, lang), 80),
    3: fechaLarga(examen && examen.fecha, lang),
    4: fechaLarga(fechaLimitePago(examen && examen.fecha, c.plazo_pago_dias, hoy), lang),
    url_button_param: String((examen && examen.alumno_id) || a.alumno_id || ""),
  };
}

/* ───────────── Lista de espera ───────────── */

const ORIGENES = ["propio", "externo"];
const MAX_MOTIVO = 200;
const AVISO_DIAS = [7, 365];
const LISTA_ESPERA_DEFECTO = Object.freeze({ enabled: false, aviso_dias: 90 });

function normalizaListaEspera(raw) {
  const r = esObjeto(raw) ? raw : {};
  const n = Number(r.aviso_dias);
  return {
    enabled: r.enabled === true,
    aviso_dias: Number.isInteger(n) && n >= AVISO_DIAS[0] && n <= AVISO_DIAS[1] ? n : LISTA_ESPERA_DEFECTO.aviso_dias,
  };
}

function validaListaEspera(raw) {
  if (raw === null) return { value: { ...LISTA_ESPERA_DEFECTO } };
  if (!esObjeto(raw)) return { error: "lista_espera_examen no tiene el formato esperado." };
  if (raw.enabled !== undefined && typeof raw.enabled !== "boolean") return { error: "lista_espera_examen.enabled debe ser true o false." };
  if (raw.aviso_dias !== undefined && !(Number.isInteger(raw.aviso_dias) && raw.aviso_dias >= AVISO_DIAS[0] && raw.aviso_dias <= AVISO_DIAS[1])) {
    return { error: `Avisar a partir de: un número de días entre ${AVISO_DIAS[0]} y ${AVISO_DIAS[1]}.` };
  }
  return { value: normalizaListaEspera(raw) };
}

/**
 * Una entrada de la lista que llega del navegador → { value } | { error }. `desde` por defecto hoy; nunca en el futuro
 * (no se espera desde mañana). `prev` = la que ya tenía (se conservan especial/motivo/origen si no vienen).
 */
function validaEntradaEspera(raw, hoy, prev) {
  if (!esObjeto(raw)) return { error: "La lista de espera no tiene el formato esperado." };
  const p = esObjeto(prev) ? prev : {};
  const prueba = raw.prueba !== undefined ? raw.prueba : p.prueba;
  if (!PRUEBAS_ESPERA.includes(prueba)) return { error: "¿Qué lista? pista, circulacion o teorico." };
  const desde = raw.desde !== undefined && raw.desde !== null && raw.desde !== "" ? raw.desde : (p.desde || hoy);
  if (!esFecha(desde)) return { error: "La fecha desde la que espera no es válida (AAAA-MM-DD)." };
  if (hoy && desde > hoy) return { error: "La fecha desde la que espera no puede ser futura." };
  if (raw.especial !== undefined && typeof raw.especial !== "boolean") return { error: "especial debe ser true o false." };
  const especial = raw.especial !== undefined ? raw.especial : p.especial === true;
  const motivo = limpiaParam(raw.motivo !== undefined ? raw.motivo : p.motivo, MAX_MOTIVO);
  const origen = raw.origen !== undefined ? raw.origen : (p.origen || "propio");
  if (!ORIGENES.includes(origen)) return { error: "origen: propio o externo." };
  const value = { prueba, desde, especial, origen };
  if (especial && motivo) value.motivo = motivo;
  return { value };
}

/**
 * Qué hace un guardado de examen con la lista de espera del alumno. PURA. → { accion, entrada?, prueba? } | null
 *   · alta de un examen pendiente de la prueba en cuya lista está → { accion: "quitar", prueba } (sale de la lista)
 *   · pasa a APTO un examen de pista → { accion: "poner", entrada: circulación desde la fecha del examen }
 *   · pasa a NO APTO (o no presentado) → { accion: "poner", entrada: esa prueba desde la fecha del examen } conservando
 *     especial/motivo/origen de la entrada con la que salió de la lista (examen.espera_salida) o de la que tenga.
 * Solo en TRANSICIONES (alta o cambio de estado): editar las notas de un suspenso no lo vuelve a meter.
 */
function cambioListaEspera({ examen, prev, esperaActual }) {
  if (!examen) return null;
  const prueba = pruebaDeExamen(examen);
  if (!prueba) return null;
  const actual = esObjeto(esperaActual) ? esperaActual : null;
  const nuevo = !prev;
  const cambioEstado = !nuevo && prev.status !== examen.status;
  if (nuevo && examen.status === "pendiente") {
    return actual && actual.prueba === prueba ? { accion: "quitar", prueba } : null;
  }
  if (!nuevo && !cambioEstado) return null;
  const fecha = esFecha(examen.fecha) ? examen.fecha : null;
  if (!fecha) return null;
  if (examen.status === "apto") {
    if (prueba !== "pista") return null;
    if (actual && actual.prueba === "circulacion") return null; // ya espera circulación: no se le cambia el «desde»
    const salida = esObjeto(examen.espera_salida) ? examen.espera_salida : {};
    return { accion: "poner", entrada: { prueba: "circulacion", desde: fecha, especial: false, origen: salida.origen === "externo" ? "externo" : "propio" } };
  }
  if (examen.status === "no_apto" || examen.status === "no_presentado") {
    const base = (actual && actual.prueba === prueba ? actual : null) || (esObjeto(examen.espera_salida) ? examen.espera_salida : {});
    const entrada = { prueba, desde: fecha, especial: base.especial === true, origen: base.origen === "externo" ? "externo" : "propio" };
    if (entrada.especial && base.motivo) entrada.motivo = limpiaParam(base.motivo, MAX_MOTIVO);
    return { accion: "poner", entrada };
  }
  return null;
}

/** Suspensos (no apto) de un alumno en una prueba, contados de sus exámenes. */
function contarSuspensos(examenes, alumno_id, prueba) {
  return (examenes || []).filter((e) => e && e.alumno_id === alumno_id && e.status === "no_apto" && pruebaDeExamen(e) === prueba).length;
}

/** Situación del chip: «suspendido» (N) > «externo» (pistas aprobadas fuera) > «primera» (nunca ha ido). */
function situacionEspera(entrada, suspensos) {
  if (Number(suspensos) > 0) return "suspendido";
  if (entrada && entrada.origen === "externo") return "externo";
  return "primera";
}

/** ¿Este alumno cuenta para la lista? (ni de baja ni aprobado del todo). */
const alumnoActivo = (a) => !!a && a.status !== "inactivo" && a.status !== "aprobado";

/**
 * Filas de UNA lista, en orden: especiales primero y, dentro de cada grupo, el que más tiempo lleva esperando.
 * [{ alumno, entrada, dias, suspensos, situacion }]
 */
function filasListaEspera({ alumnos, examenes, prueba, hoy }) {
  const out = [];
  for (const a of alumnos || []) {
    const e = a && a.espera_examen;
    if (!alumnoActivo(a) || !esObjeto(e) || e.prueba !== prueba) continue;
    const suspensos = contarSuspensos(examenes, a.alumno_id, prueba);
    out.push({ alumno: a, entrada: e, dias: diasEntre(e.desde, hoy) || 0, suspensos, situacion: situacionEspera(e, suspensos) });
  }
  out.sort((x, y) => (x.entrada.especial === true ? 0 : 1) - (y.entrada.especial === true ? 0 : 1)
    || String(x.entrada.desde || "").localeCompare(String(y.entrada.desde || ""))
    || String(x.alumno.name || "").localeCompare(String(y.alumno.name || "")));
  return out;
}

/** { total, media_dias, mas_de } de una lista (más_de = cuántos pasan del umbral de aviso). */
function resumenLista(filas, avisoDias) {
  const total = (filas || []).length;
  const media = total ? Math.round(filas.reduce((s, f) => s + (f.dias || 0), 0) / total) : 0;
  const mas = avisoDias ? filas.filter((f) => f.dias > avisoDias).length : 0;
  return { total, media_dias: media, mas_de: mas };
}

/** Alumnos de TODAS las listas que pasan de `aviso_dias` (para el Dashboard). */
function esperandoDemasiado({ alumnos, cfg, hoy }) {
  const c = normalizaListaEspera(cfg);
  if (!c.enabled) return [];
  const out = [];
  for (const a of alumnos || []) {
    const e = a && a.espera_examen;
    if (!alumnoActivo(a) || !esObjeto(e) || !PRUEBAS_ESPERA.includes(e.prueba)) continue;
    const dias = diasEntre(e.desde, hoy);
    if (dias != null && dias > c.aviso_dias) out.push({ alumno: a, entrada: e, dias });
  }
  return out;
}

/**
 * Eventos de la campanita (tipo «espera_examen») para quien pasa del umbral. Clave DETERMINISTA por alumno, lista,
 * fecha y umbral: el cron lo ve cada día y c_notifyWebSocket descarta el repetido (una vez por alumno y umbral; si
 * vuelve a la lista con otra fecha, es otra espera y vuelve a avisar).
 */
function avisosEsperaLarga({ alumnos, cfg, hoy }) {
  const c = normalizaListaEspera(cfg);
  return esperandoDemasiado({ alumnos, cfg, hoy }).map(({ alumno, entrada }) => ({
    tipo: "espera_examen",
    id: `esp-${alumno.alumno_id}-${entrada.prueba}-${entrada.desde}-${c.aviso_dias}`,
    orden_ts: new Date(ymdUtc(entrada.desde) + (c.aviso_dias + 1) * DIA_MS + 7 * 3600000).toISOString(),
    alumno_id: alumno.alumno_id,
    alumno_nombre: alumno.name || "",
    fecha: entrada.desde,
    detalle: entrada.prueba,
    cantidad: c.aviso_dias,
    ...(entrada.especial ? { concepto: "especial" } : {}),
  }));
}

export {
  PRUEBAS_PRACTICO, PRUEBAS_ESPERA, OFICINA_PRUEBAS, MAX_TELEFONOS, PLAZO_PAGO, AVISO_DIAS, ORIGENES, AVISO_OFICINA_DEFECTO, LISTA_ESPERA_DEFECTO, validaPrueba, numeroIntento, pruebaDeExamen, esFecha, diasEntre, fechaLarga, fechaLimitePago, descripcionExamen, normalizaTelefono, normalizaAvisoOficina, validaAvisoOficina, encajaAvisoOficina, tocaAvisoOficina, etiquetaAlumno, paramsAvisoOficina, normalizaListaEspera, validaListaEspera, validaEntradaEspera, cambioListaEspera, contarSuspensos, situacionEspera, filasListaEspera, resumenLista, esperandoDemasiado, avisosEsperaLarga,
};
